import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ReportSelector from './components/ReportSelector';
import ReportToolbar from './components/ReportToolbar';
import ReportVisualization from './components/ReportVisualization';
import ReportSummaryTable from './components/ReportSummaryTable';
import ScheduleReportModal from './components/ScheduleReportModal';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ReportsAnalytics = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [favoriteReports, setFavoriteReports] = useState([
    { categoryId: 'card-usage', reportId: 'card-activity' },
    { categoryId: 'payment-analytics', reportId: 'payment-summary' }
  ]);
  const [dateRange, setDateRange] = useState({
    preset: 'last-30-days',
    startDate: '',
    endDate: ''
  });
  const [filters, setFilters] = useState({
    cardStatus: 'all',
    userType: 'all',
    paymentMethod: 'all',
    region: ''
  });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [showMobileSelector, setShowMobileSelector] = useState(false);

  // Initialize with a default report
  useEffect(() => {
    setSelectedReport({ categoryId: 'card-usage', reportId: 'card-activity' });
  }, []);

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    setShowMobileSelector(false);
  };

  const handleFavoriteToggle = (categoryId, reportId) => {
    const isCurrentlyFavorite = favoriteReports.some(
      fav => fav.categoryId === categoryId && fav.reportId === reportId
    );

    if (isCurrentlyFavorite) {
      setFavoriteReports(prev => 
        prev.filter(fav => !(fav.categoryId === categoryId && fav.reportId === reportId))
      );
    } else {
      setFavoriteReports(prev => [...prev, { categoryId, reportId }]);
    }
  };

  const handleExport = (format) => {
    // Simulate export functionality
    console.log(`Exporting report as ${format}`);
    
    // In a real application, this would trigger a download
    const reportName = selectedReport ? `${selectedReport.categoryId}-${selectedReport.reportId}` : 'report';
    const fileName = `${reportName}-${new Date().toISOString().split('T')[0]}.${format}`;
    
    // Create a mock download
    const link = document.createElement('a');
    link.href = '#';
    link.download = fileName;
    link.click();
  };

  const handleShare = () => {
    // Simulate share functionality
    if (navigator.share) {
      navigator.share({
        title: 'NFC Admin Report',
        text: 'Check out this analytics report',
        url: window.location.href
      });
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      alert('Report URL copied to clipboard');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleScheduleReport = (scheduleInfo) => {
    setScheduledReports(prev => [...prev, scheduleInfo]);
    console.log('Report scheduled:', scheduleInfo);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Reports & Analytics - NFC Card Admin</title>
        <meta name="description" content="Comprehensive analytics and reporting dashboard for NFC card management system" />
      </Helmet>

      <Header />
      <Sidebar />

      <div className="lg:ml-60 pt-16">
        <div className="flex h-[calc(100vh-4rem)]">
          {/* Desktop Report Selector - Left Panel */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <ReportSelector
              selectedReport={selectedReport}
              onReportSelect={handleReportSelect}
              onFavoriteToggle={handleFavoriteToggle}
              favoriteReports={favoriteReports}
            />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col">
            {/* Mobile Report Selector Toggle */}
            <div className="lg:hidden p-4 border-b border-border bg-surface">
              <Button
                variant="outline"
                onClick={() => setShowMobileSelector(!showMobileSelector)}
                iconName="BarChart3"
                iconPosition="left"
                className="w-full justify-start"
              >
                {selectedReport ? 'Change Report' : 'Select Report'}
              </Button>
            </div>

            {/* Mobile Report Selector Dropdown */}
            {showMobileSelector && (
              <div className="lg:hidden border-b border-border bg-surface max-h-80 overflow-y-auto">
                <ReportSelector
                  selectedReport={selectedReport}
                  onReportSelect={handleReportSelect}
                  onFavoriteToggle={handleFavoriteToggle}
                  favoriteReports={favoriteReports}
                />
              </div>
            )}

            {/* Report Toolbar */}
            <ReportToolbar
              selectedReport={selectedReport}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              filters={filters}
              onFiltersChange={setFilters}
              onExport={handleExport}
              onShare={handleShare}
              onPrint={handlePrint}
              onScheduleReport={() => setShowScheduleModal(true)}
            />

            {/* Content Area */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
              {/* Visualization Panel */}
              <div className="flex-1 min-h-0">
                <ReportVisualization
                  selectedReport={selectedReport}
                  dateRange={dateRange}
                  filters={filters}
                />
              </div>
            </div>

            {/* Summary Table */}
            <div className="border-t border-border bg-background p-4">
              <ReportSummaryTable
                selectedReport={selectedReport}
                dateRange={dateRange}
                filters={filters}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Report Modal */}
      <ScheduleReportModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        selectedReport={selectedReport}
        onSchedule={handleScheduleReport}
      />

      {/* Floating Action Button for Mobile */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <Button
          variant="primary"
          onClick={() => setShowScheduleModal(true)}
          disabled={!selectedReport}
          className="w-14 h-14 rounded-full shadow-elevation-3"
        >
          <Icon name="Clock" size={24} />
        </Button>
      </div>
    </div>
  );
};

export default ReportsAnalytics;